// src/hooks/useBlogs.js - FIXED

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  getBlogs, 
  getCategories, 
  getTags,
  searchBlogs,
  getFeaturedBlogs
} from '../services/blogServices';

export const useBlogs = () => {
  // Data states
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTag, setActiveTag] = useState(null);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery);
      } else {
        setSearchResults(null);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [blogsRes, categoriesRes, tagsRes, featuredRes] = await Promise.allSettled([
        getBlogs(),
        getCategories(),
        getTags(),
        getFeaturedBlogs()
      ]);

      // Handle blogs - FIXED: More robust data extraction
      let blogsData = [];
      if (blogsRes.status === 'fulfilled') {
        const data = blogsRes.value;
        blogsData = data?.data?.results || data?.data || data || [];
      }
      setBlogs(Array.isArray(blogsData) ? blogsData : []);
      
      // Calculate total pages correctly
      const totalCount = blogsRes.status === 'fulfilled' 
        ? (blogsRes.value?.data?.count || blogsRes.value?.count || blogsData.length || 0)
        : 0;
      setTotalPages(Math.max(1, Math.ceil(totalCount / 12)));

      // Handle categories
      let categoriesData = [];
      if (categoriesRes.status === 'fulfilled') {
        const data = categoriesRes.value;
        categoriesData = data?.data?.results || data?.data || data || [];
      }
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);

      // Handle tags
      let tagsData = [];
      if (tagsRes.status === 'fulfilled') {
        const data = tagsRes.value;
        tagsData = data?.data?.results || data?.data || data || [];
      }
      setTags(Array.isArray(tagsData) ? tagsData : []);

      // Handle featured
      let featuredData = [];
      if (featuredRes.status === 'fulfilled') {
        const data = featuredRes.value;
        featuredData = data?.data?.results || data?.data || data || [];
      }
      setFeaturedBlog(Array.isArray(featuredData) && featuredData.length > 0 
        ? featuredData[0] 
        : null);

    } catch (err) {
      console.error('Failed to fetch blog data:', err);
      setError('Unable to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    setError(null);

    try {
      const response = await searchBlogs(query);
      const results = response?.data?.results || response?.data || response || [];
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
  }, []);

  const clearFilters = useCallback(() => {
    setActiveCategory('all');
    setActiveTag(null);
  }, []);

  const changePage = async(page) => {
    setLoading(true);
    try {
      const response = await getBlogs({page: page});
      const data = response?.data?.results || response?.data || response || [];
      setBlogs(Array.isArray(data) ? data : []);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Failed to change page:', error);
      setError('Failed to load page. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get displayed posts based on active filters - FIXED: Add safety checks
  const getDisplayedPosts = useCallback(() => {
    // Ensure blogs is an array
    const safeBlogs = Array.isArray(blogs) ? blogs : [];
    
    // Use search results if available, otherwise use all blogs
    let posts = searchResults !== null ? searchResults : safeBlogs;
    
    // Ensure posts is an array
    posts = Array.isArray(posts) ? posts : [];

    // Apply category filter
    if (activeCategory && activeCategory !== 'all') {
      posts = posts.filter(post => {
        if (!post) return false;
        const categorySlug = typeof post.category === 'string' 
          ? post.category 
          : post.category?.slug;
        return categorySlug === activeCategory;
      });
    }

    // Apply tag filter
    if (activeTag) {
      posts = posts.filter(post => {
        if (!post || !post.tags || !Array.isArray(post.tags)) return false;
        
        return post.tags.some(tag => {
          if (!tag) return false;
          if (typeof tag === 'string') {
            return tag === activeTag;
          }
          return tag.slug === activeTag || tag.id === activeTag || tag.name === activeTag;
        });
      });
    }

    return posts;
  }, [blogs, searchResults, activeCategory, activeTag]);

  // Memoized recent posts - FIXED: Add safety
  const recentPosts = useMemo(() => {
    const safeBlogs = Array.isArray(blogs) ? blogs : [];
    
    return safeBlogs
      .filter(blog => blog?.published_at)
      .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
      .slice(0, 4);
  }, [blogs]);

  // Build complete categories list - FIXED: Add safety
  const categoriesList = useMemo(() => {
    const safeBlogs = Array.isArray(blogs) ? blogs : [];
    const safeCategories = Array.isArray(categories) ? categories : [];
    
    const blogCategoryMap = new Map();
    
    safeBlogs.forEach(blog => {
      const category = blog?.category;
      if (category?.slug) {
        const count = blogCategoryMap.get(category.slug) || 0;
        blogCategoryMap.set(category.slug, count + 1);
      }
    });
    
    const apiCategories = safeCategories.map(cat => ({
      id: cat?.slug || cat?.id || 'unknown',
      name: cat?.name || cat?.title || 'Uncategorized',
      slug: cat?.slug || cat?.id || 'unknown',
      count: blogCategoryMap.get(cat?.slug) || 0
    }));
    
    const blogCategories = Array.from(blogCategoryMap.entries())
      .filter(([slug]) => !apiCategories.some(c => c.slug === slug))
      .map(([slug, count]) => ({
        id: slug,
        name: slug.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '),
        slug,
        count
      }));
    
    const merged = [...apiCategories, ...blogCategories];
    merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    return [
      { id: 'all', name: 'All Posts', slug: 'all', count: safeBlogs.length },
      ...merged
    ];
  }, [blogs, categories]);

  // Build tags list - FIXED: Add safety
  const tagsList = useMemo(() => {
    const safeBlogs = Array.isArray(blogs) ? blogs : [];
    const tagCounts = new Map();
    
    safeBlogs.forEach(blog => {
      if (blog?.tags && Array.isArray(blog.tags)) {
        blog.tags.forEach(tag => {
          if (!tag) return;

          let tagSlug = null;
          let tagName = null;
          
          if (typeof tag === 'string') {
            tagSlug = tag;
            tagName = tag;
          } else if (typeof tag === 'object') {
            tagSlug = tag?.slug || tag?.id || tag?.name || null;
            tagName = tag?.name || tag?.title || tagSlug;
          }
          
          if (tagSlug) {
            const existing = tagCounts.get(tagSlug);
            tagCounts.set(tagSlug, {
              slug: tagSlug,
              name: tagName || tagSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              count: (existing?.count || 0) + 1
            });
          }
        });
      }
    });
    
    return Array.from(tagCounts.values())
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [blogs]);

  return {
    // Data
    blogs,
    categories,
    tags,
    featuredBlog,
    recentPosts,
    categoriesList,
    tagsList,

    //Pagination
    currentPage,
    totalPages,
    changePage,
    
    // Filter states and setters
    activeCategory,
    setActiveCategory,
    activeTag,
    setActiveTag,
    clearFilters,
    
    // Search
    searchQuery,
    setSearchQuery,
    searchResults,
    searching,
    clearSearch,
    
    // UI State
    loading,
    error,
    
    // Helpers
    getDisplayedPosts,
    refreshData: fetchInitialData
  };
};